import { PlusOutlined } from '@ant-design/icons'
import { Tooltip } from 'antd'
import { FunctionComponent } from 'react'
import styled from 'styled-components'
import Blur from '@/components/common/blur'
import {
  KolThumbnail,
  type KolThumbnailProps,
} from '@/components/common/kol-thumbnail'
import SafeNewTabLink from '@/components/safe-new-tab-link'
import routes from '@/hooks/use-authorization/routes'
import { useIntl } from '@/i18n/hooks/use-intl'

interface KolThumbnailItem {
  uuid: string
  tooltipTitle?: string
  blur?: boolean
  href?: string
}

interface KolThumbnailGroupProps extends Pick<KolThumbnailProps, 'imageSize'> {
  data: KolThumbnailItem[]
  maxCount?: number
  onClickMaximumItem?: (data: KolThumbnailItem) => void
  from?: string
  isDemo?: boolean
}

const KolThumbnailGroup: FunctionComponent<KolThumbnailGroupProps> = ({
  data,
  maxCount = 5,
  onClickMaximumItem,
  from,
  isDemo = false,
  imageSize = 'small',
}) => {
  const { formatMessage } = useIntl()

  return (
    <Wrapper>
      {data
        .slice(0, maxCount)
        .map(
          (
            {
              uuid,
              tooltipTitle,
              blur = false,
              href = routes.kolId.stringifyUrl({ kolId: uuid }, { from }),
            },
            index,
          ) => {
            const isMaxCount = index === maxCount - 1
            const hasSeeMoreAction = isMaxCount && onClickMaximumItem

            return (
              <Tooltip
                key={uuid}
                placement='bottom'
                title={
                  hasSeeMoreAction
                    ? formatMessage({ id: 'kol:kol_price_see_more' })
                    : tooltipTitle
                }
              >
                <Blur activate={blur}>
                  <SafeNewTabLink
                    href={isMaxCount || isDemo || blur ? undefined : href}
                  >
                    <KolThumbnail imageSize={imageSize} kolUuid={uuid}>
                      {hasSeeMoreAction && !blur && (
                        <PlusOutlined
                          onClick={(event): void => {
                            event.stopPropagation()
                            onClickMaximumItem({ uuid, tooltipTitle })
                          }}
                        />
                      )}
                    </KolThumbnail>
                  </SafeNewTabLink>
                </Blur>
              </Tooltip>
            )
          },
        )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  align-items: center;

  ${SafeNewTabLink} {
    position: relative;
    cursor: pointer;
    margin: 0 -8px 0 0;
    height: 100%;

    ${KolThumbnail} {
      position: relative;
    }

    .anticon-plus {
      position: absolute;
      color: white;
      background-color: rgb(0, 0, 0, 0.4);
      border-radius: 100%;
      top: 0px;
      bottom: 0px;
      left: 0px;
      right: 0px;

      svg {
        margin: auto;
      }
    }
  }
`

export default KolThumbnailGroup
